import { FC, ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import classNames from "classnames";

import { isPathWithinBasePath } from "../../../../../utilities/isPathWithinBasePath";

import styles from "./styles.module.scss";

/** The `SidebarNavSectionItem` properties. */
export interface SidebarNavSectionItemProps {
  /** The id of the card. */
  readonly id?: string;

  /** The sidebar item displayed on this drawer item. */
  readonly path?: string;

  /** The optional test id of the navigation sidebar item. */
  readonly testId?: string;

  /** The optional switch to disable exact path matching. */
  readonly nonExactMatch?: boolean;

  /**
   * The optional base path that keeps the item highlighted while the current route is that base path or one of its
   * descendants. Use this only when the highlighted routes differ from `path`, such as a `/` item that must stay
   * highlighted across `/employee/:id`. To keep an item highlighted on its own child routes, use `nonExactMatch`.
   */
  readonly activeBasePath?: string;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The in-context sidebar navigation component. */
export const SidebarNavSectionItem: FC<SidebarNavSectionItemProps> = (props) => {
  const { id, testId, children, path, nonExactMatch = false, activeBasePath } = props;
  const location = useLocation();

  const isBasePathActive = !!activeBasePath && isPathWithinBasePath(location.pathname, activeBasePath);

  if (path) {
    return (
      <NavLink
        id={id}
        to={path}
        end={!nonExactMatch}
        data-testid={testId ? `test__${testId}-nav-sidebar-item` : undefined}
        className={({ isActive }) => classNames(styles.SideBarNavItem, "nav-sidebar-item", { active: isActive || isBasePathActive })}
      >
        {children}
      </NavLink>
    );
  } else {
    return (
      <div
        id={id}
        className={classNames(styles.SideBarNavItem, "nav-sidebar-item")}
        data-testid={testId ? `test__${testId}-nav-sidebar-item` : undefined}
      >
        {children}
      </div>
    );
  }
};
